import * as React from "react";
import { CognitoUserPoolType, InternalUser, UserAccountsService, UserGroup } from "gen/clients";
import Drawer from "@mui/material/Drawer";
import { DrawerHeader } from "components/DrawerHeader/DrawerHeader";
import { DrawerBody } from "components/DrawerBody/DrawerBody";
import { useMutation, useQuery } from "react-query";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { LabelValue } from "components/LabelValue/LabelValue";
import Divider from "@mui/material/Divider";
import { CognitoStateSwitch } from "components/cognitoComponents/CognitoStateSwitch/CognitoStateSwitch";
import { EmailVerifiedSwitch } from "components/cognitoComponents/EmailVerifiedSwitch/EmailVerifiedSwitch";
import { formatEpoch } from "utils/dateUtils";
import { Box, Button, Toolbar } from "@mui/material";
import { LoadingButton } from "components/LoadingButton/LoadingButton";
import { useSnackbarAlert } from "hooks/useSnackbarAlert";
import { useDialogState } from "hooks/useDialogState";
import { EditAttributesDialog } from "./EditAttributesDialog/EditAttributesDialog";
import { SetPasswordDialog } from "components/cognitoComponents/SetPasswordDialog/SetPasswordDialog";
import { CreateCognitoUserDialog } from "components/cognitoComponents/CreateCognitoUserDialog/CreateCognitoUserDialog";
import { SnackbarApiError } from "components/SnackbarApiError/SnackbarApiError";
import { useTranslation } from "react-i18next";
import { EditUserGroupsDialog } from "../../../components/cognitoComponents/EditUserGroupsDialog/EditUserGroupsDialog";
import { MfaEnabledSwitch } from "./MfaEnabledSwitch/MfaEnabledSwitch";

interface Props {
  user: InternalUser;
  onClose: () => void;
}

const StaffAccountDetailsPanel: React.FC<Props> = ({ user, onClose }) => {
  const { t } = useTranslation();
  const [isCreateCognitoUserDialogOpen, openCreateCognitoUserDialog, closeCreateCognitoUserDialog] = useDialogState();
  const [isSetPasswordDialogOpen, openSetPasswordDialog, closeSetPasswordDialog] = useDialogState();
  const [isEditAttributesDialogOpen, openEditAttributesDialog, closeEditAttributesDialog] = useDialogState();
  const [isEditUserGroupsDialogOpen, openEditUserGroupsDialog, closeEditUserGroupsDialog] = useDialogState();

  const { snackbarAlert, openSnackbarAlert, closeSnackbarAlert } = useSnackbarAlert({
    autoHideDuration: 5000,
    vertical: "bottom",
    horizontal: "left"
  });

  const {
    data: cognitoUserCollection,
    refetch: refetchCognitoUserCollection,
    isLoading: isCognitoUserSummaryLoading,
    error: cognitoUserCollectionError
  } = useQuery(["listCognitoUsers", user.email], {
    queryFn: () =>
      UserAccountsService.listCognitoUsers({ userPool: CognitoUserPoolType.INTERNAL_USER_POOL, email: user.email })
  });

  const cognitoUserSummary = React.useMemo(() => cognitoUserCollection?.items[0], [cognitoUserCollection]);

  const {
    data: cognitoUser,
    refetch: refetchCognitoUser,
    isLoading: isCognitoUserLoading,
    error: cognitoUserError
  } = useQuery(["getCognitoUser", cognitoUserSummary?.username], {
    queryFn: () =>
      UserAccountsService.getCognitoUser({
        userPool: CognitoUserPoolType.INTERNAL_USER_POOL,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        cognitoUsername: encodeURIComponent(cognitoUserSummary!.username)
      }),
    enabled: cognitoUserSummary?.username !== undefined
  });

  const isLoading = isCognitoUserSummaryLoading || isCognitoUserLoading;
  const loadingError = cognitoUserCollectionError || cognitoUserError;

  const {
    mutate: confirmUserAccount,
    isLoading: isConfirmUserAccountLoading,
    error: confirmUserAccountError
  } = useMutation(UserAccountsService.confirmCognitoUserSignUp, { onSuccess: refetchCognitoUser });

  const onEditAttributesSuccess = React.useCallback(() => {
    closeEditAttributesDialog();
    openSnackbarAlert({ message: "Operation successful", severity: "success" });
    refetchCognitoUser();
  }, [closeEditAttributesDialog, openSnackbarAlert, refetchCognitoUser]);

  const onEditUserGroupsSuccess = React.useCallback(() => {
    closeEditUserGroupsDialog();
    openSnackbarAlert({ message: "Operation successful", severity: "success" });
    refetchCognitoUser();
  }, [closeEditUserGroupsDialog, openSnackbarAlert, refetchCognitoUser]);

  const onSetPasswordSuccess = React.useCallback(() => {
    closeSetPasswordDialog();
    openSnackbarAlert({ message: "Operation successful", severity: "success" });
    refetchCognitoUser();
  }, [closeSetPasswordDialog, openSnackbarAlert, refetchCognitoUser]);

  const onCreateCognitoUserSuccess = React.useCallback(() => {
    closeCreateCognitoUserDialog();
    openSnackbarAlert({ message: "Operation successful", severity: "success" });
    refetchCognitoUserCollection();
  }, [closeCreateCognitoUserDialog, refetchCognitoUserCollection, openSnackbarAlert]);

  return (
    <>
      {snackbarAlert}
      <Drawer open={true} onClose={onClose} anchor="right" PaperProps={{ sx: { width: "60vw" } }}>
        <DrawerHeader onClose={onClose} title={`Account Details: ${user.firstName} ${user.lastName} (${user.email})`} />
        <DrawerBody isLoading={isLoading} error={loadingError}>
          <Stack spacing={2}>
            {/* XTRF Account Section */}
            <Typography variant="h6">XTRF Account</Typography>
            <LabelValue label="First Name" value={user.firstName} />
            <LabelValue label="Last Name" value={user.lastName} />
            <LabelValue label="Email" value={user.email} />
            <LabelValue label="Status" value={user.status === "ACTIVE" ? "Active" : "Disabled"} />
            <LabelValue label="XTRF ID" value={user.xtrfUserId} />

            {/* Divider */}
            <Divider />

            {/* Cognito Account Section */}
            {cognitoUserSummary && cognitoUser ? (
              <>
                <Toolbar disableGutters={true} variant="dense">
                  <Typography variant="h6">Cognito Account</Typography>
                  <Box sx={{ flexGrow: 1 }} />
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        closeSnackbarAlert();
                        openSetPasswordDialog();
                      }}
                    >
                      Set Password
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        closeSnackbarAlert();
                        openEditAttributesDialog();
                      }}
                    >
                      Edit Attributes
                    </Button>
                    <LoadingButton
                      variant="outlined"
                      disabled={cognitoUser.userStatus !== "UNCONFIRMED"}
                      onClick={() =>
                        confirmUserAccount({
                          userPool: CognitoUserPoolType.INTERNAL_USER_POOL,
                          cognitoUsername: encodeURIComponent(cognitoUser.username)
                        })
                      }
                      isLoading={isConfirmUserAccountLoading}
                      type="button"
                    >
                      Confirm User Account
                    </LoadingButton>
                  </Stack>
                </Toolbar>
                <LabelValue label="Username" value={cognitoUserSummary.username} />
                <LabelValue
                  label="Roles"
                  value={
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Box>
                        {cognitoUser.groups
                          ?.map(g => t(`roles.${g}`))
                          .sort((s1, s2) => s1.localeCompare(s2))
                          .join(", ") || "None"}
                      </Box>
                      <Button variant="outlined" onClick={openEditUserGroupsDialog}>
                        Edit Roles
                      </Button>
                    </Stack>
                  }
                />
                <LabelValue
                  label="Enabled"
                  value={
                    <CognitoStateSwitch
                      userPool={CognitoUserPoolType.INTERNAL_USER_POOL}
                      cognitoUser={cognitoUser}
                      onSuccess={refetchCognitoUser}
                    />
                  }
                />
                <LabelValue
                  label="Email Verified"
                  value={
                    <EmailVerifiedSwitch
                      userPool={CognitoUserPoolType.INTERNAL_USER_POOL}
                      cognitoUser={cognitoUser}
                      onSuccess={refetchCognitoUser}
                    />
                  }
                />
                <LabelValue
                  label="MFA Enabled"
                  value={<MfaEnabledSwitch cognitoUser={cognitoUser} onSuccess={refetchCognitoUser} />}
                />
                <LabelValue label="Account Status" value={cognitoUserSummary.userStatus} />
                {cognitoUser?.lastVisit && (
                  <LabelValue label="Last Visit" value={formatEpoch(cognitoUser?.lastVisit)} />
                )}
                <LabelValue
                  label="Attributes"
                  verticalAlignment="top"
                  value={
                    <Stack spacing={1}>
                      {cognitoUser.firstName && (
                        <LabelValue
                          label="First Name"
                          value={
                            <Typography
                              sx={theme =>
                                cognitoUser.firstName !== `${user.firstName}`
                                  ? {
                                      color: theme.palette.error.main,
                                      fontWeight: "bold"
                                    }
                                  : {}
                              }
                            >
                              {cognitoUser.firstName}
                            </Typography>
                          }
                        />
                      )}
                      {cognitoUser.lastName && (
                        <LabelValue
                          label="Last Name"
                          value={
                            <Typography
                              sx={theme =>
                                cognitoUser.lastName !== `${user.lastName}`
                                  ? {
                                      color: theme.palette.error.main,
                                      fontWeight: "bold"
                                    }
                                  : {}
                              }
                            >
                              {cognitoUser.lastName}
                            </Typography>
                          }
                        />
                      )}
                      {cognitoUser.email && (
                        <LabelValue
                          label="Email"
                          value={
                            <Typography
                              sx={theme =>
                                cognitoUser.email !== `${user.email}`
                                  ? {
                                      color: theme.palette.error.main,
                                      fontWeight: "bold"
                                    }
                                  : {}
                              }
                            >
                              {cognitoUser.email}
                            </Typography>
                          }
                        />
                      )}
                      {cognitoUser.phoneNumber && (
                        <LabelValue label="Phone Number" value={<Typography>{cognitoUser.phoneNumber}</Typography>} />
                      )}
                    </Stack>
                  }
                />
              </>
            ) : (
              <>
                <Typography variant="h6">Cognito Account</Typography>
                <Box textAlign="center" mt={3}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      closeSnackbarAlert();
                      openCreateCognitoUserDialog();
                    }}
                  >
                    Create Cognito Account
                  </Button>
                </Box>
              </>
            )}
          </Stack>
        </DrawerBody>
      </Drawer>

      {isEditAttributesDialogOpen && cognitoUser && (
        <EditAttributesDialog
          username={cognitoUser.username}
          firstName={cognitoUser.firstName}
          lastName={cognitoUser.lastName}
          email={cognitoUser.email}
          phoneNumber={cognitoUser.phoneNumber}
          onSuccess={onEditAttributesSuccess}
          onClose={closeEditAttributesDialog}
        />
      )}

      {isSetPasswordDialogOpen && cognitoUser && (
        <SetPasswordDialog
          userPool={CognitoUserPoolType.INTERNAL_USER_POOL}
          username={cognitoUser.username}
          onSuccess={onSetPasswordSuccess}
          onClose={closeSetPasswordDialog}
        />
      )}

      {isCreateCognitoUserDialogOpen && (
        <CreateCognitoUserDialog
          userPool={CognitoUserPoolType.INTERNAL_USER_POOL}
          username={user.email}
          firstName={user.firstName}
          lastName={user.lastName}
          email={user.email}
          askPhoneNumber={true}
          groups={[]}
          onSuccess={onCreateCognitoUserSuccess}
          onClose={closeCreateCognitoUserDialog}
        />
      )}

      {isEditUserGroupsDialogOpen && cognitoUser && (
        <EditUserGroupsDialog
          userPool={CognitoUserPoolType.INTERNAL_USER_POOL}
          username={cognitoUser.username}
          userGroups={(cognitoUser.groups as UserGroup[]) || []}
          availableGroups={[
            UserGroup.OPERATOR,
            UserGroup.EXECUTIVE,
            UserGroup.POWER_USER,
            UserGroup.ADMIN,
            UserGroup.SUPER_USER
          ]}
          onSuccess={onEditUserGroupsSuccess}
          onClose={closeEditUserGroupsDialog}
        />
      )}

      {confirmUserAccountError && <SnackbarApiError error={confirmUserAccountError} />}
    </>
  );
};

export { StaffAccountDetailsPanel };
